import React, { useState, useEffect } from 'react';
import { Activity, ChevronDown, FileText, RefreshCw, Tag } from 'lucide-react';
import { apiService, CancerType, Indication } from '../services/api';
import { IndicationInfo } from '../types';

interface SelectionToolbarProps {
  selectedCancerType: string;
  selectedIndication: string;
  selectedNccnType: string;
  onSelectionChange: (cancerType: string, indication: string, nccnType: string) => void;
  loading?: boolean;
  indicationInfo?: IndicationInfo | null;
  uniquePmidCount?: number;
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  selectedCancerType,
  selectedIndication,
  selectedNccnType,
  onSelectionChange,
  loading = false,
  indicationInfo = null,
  uniquePmidCount = 0,
}) => {
  const [cancerTypes, setCancerTypes] = useState<CancerType[]>([]);
  const [indications, setIndications] = useState<Indication[]>([]);
  const [loadingIndications, setLoadingIndications] = useState(false);

  useEffect(() => {
    const fetchCancerTypes = async () => {
      try {
        const data = await apiService.fetchCancerTypes();
        if (Array.isArray(data)) {
          setCancerTypes(data);
        } else {
          console.error('Cancer types API returned non-array:', data);
          setCancerTypes([]);
        }
      } catch (error) {
        console.error('Error fetching cancer types:', error);
        setCancerTypes([]);
      }
    };
    fetchCancerTypes();
  }, []);

  useEffect(() => {
    const fetchIndications = async () => {
      if (!selectedCancerType) {
        setIndications([]);
        return;
      }

      try {
        setLoadingIndications(true);
        const data = await apiService.fetchIndications(selectedCancerType);
        if (Array.isArray(data)) {
          setIndications(data);
          if (selectedIndication && !data.find((ind) => ind.id === selectedIndication)) {
            onSelectionChange(selectedCancerType, '', selectedNccnType);
          }
        } else {
          console.error('Indications API returned non-array:', data);
          setIndications([]);
        }
      } catch (error) {
        console.error('Error fetching indications:', error);
        setIndications([]);
      } finally {
        setLoadingIndications(false);
      }
    };

    fetchIndications();
  }, [selectedCancerType]);

  const handleCancerTypeChange = (cancerType: string) => {
    onSelectionChange(cancerType, '', selectedNccnType);
  };

  const handleIndicationChange = (indication: string) => {
    onSelectionChange(selectedCancerType, indication, selectedNccnType);
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100">
      <div className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-3">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4">
          <div className="flex items-center gap-2 flex-shrink-0 lg:pb-1.5">
            <div>
              <h3 className="text-base font-semibold text-slate-900 leading-tight">Order report</h3>
              <p className="text-xs text-slate-500">Choose a cancer type and indication.</p>
            </div>
            {loading && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500 ml-1">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Loading...</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 min-w-0">
            <label className="space-y-1 text-sm text-slate-600">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Cancer type
              </span>
              <div className="relative">
                <select
                  value={selectedCancerType}
                  onChange={(e) => handleCancerTypeChange(e.target.value)}
                  className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 cursor-pointer"
                  disabled={loading}
                >
                  <option value="">Select cancer type</option>
                  {Array.isArray(cancerTypes) &&
                    cancerTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </label>

            <label className="space-y-1 text-sm text-slate-600">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Indication
              </span>
              <div className="relative">
                <select
                  value={selectedIndication}
                  onChange={(e) => handleIndicationChange(e.target.value)}
                  className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                  disabled={!selectedCancerType || loadingIndications || loading}
                >
                  <option value="">
                    {loadingIndications
                      ? 'Loading...'
                      : !selectedCancerType
                        ? 'Select cancer type first'
                        : 'Select indication'}
                  </option>
                  {Array.isArray(indications) &&
                    indications.map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.name}
                      </option>
                    ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </label>
          </div>
        </div>

        {indicationInfo && (
          <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 text-slate-600 rounded-lg border border-slate-200 text-[11px] font-semibold max-w-full">
              <Tag className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="uppercase text-slate-400 tracking-wider font-bold text-[9px]">
                Indication
              </span>
              <span className="truncate max-w-[280px] sm:max-w-[400px]">{indicationInfo.name}</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white text-indigo-900 rounded-lg border border-indigo-100 text-[11px] font-semibold">
              <FileText className="w-3 h-3 text-indigo-600" />
              <span className="uppercase text-indigo-400 tracking-wider font-bold text-[9px]">
                Guideline
              </span>
              <span className="whitespace-nowrap">NCCN v{indicationInfo.nccnVersion}</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100 text-[11px] font-semibold">
              <Activity className="w-3 h-3 text-emerald-600" />
              <span className="uppercase text-emerald-500 tracking-wider font-bold text-[9px]">
                Unique PMIDs
              </span>
              <span>{uniquePmidCount}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectionToolbar;
